<template>
  <div>
    <b-row>
      <b-col cols="12" sm="6" lg="3" class="mb-3">
        <b-card class="h-100 border-0 shadow-sm">
          <div class="text-muted small">Miembros activos</div>
          <div class="h3 mb-1">{{ active.members || 0 }}</div>
          <div class="small text-muted">
            {{ active.families || 0 }} familias · {{ active.companies || 0 }} empresas ({{ active.seats || 0 }} cupos) ·
            {{ active.subscriptions || 0 }} suscripciones
          </div>
        </b-card>
      </b-col>

      <b-col cols="12" sm="6" lg="3" class="mb-3">
        <b-card class="h-100 border-0 shadow-sm">
          <div class="text-muted small">Nuevos en el periodo</div>
          <div class="h3 mb-1">{{ periodNew.members || 0 }}</div>
          <div class="small text-muted">
            {{ periodNew.subscriptions || 0 }} suscripciones nuevas · {{ periodCanceled.subscriptions || 0 }} canceladas
            <span v-if="periodCanceled.by_guarantee">({{ periodCanceled.by_guarantee }} por garantía)</span>
          </div>
        </b-card>
      </b-col>

      <b-col cols="12" sm="6" lg="3" class="mb-3">
        <b-card class="h-100 border-0 shadow-sm" :class="{ 'attention-card': attentionTotal > 0 }">
          <div class="text-muted small">Requieren atención</div>
          <div class="h3 mb-1">{{ attentionTotal }}</div>
          <div class="small">
            {{ attention.payment_failed || 0 }} con pago fallido · {{ attention.cancel_scheduled || 0 }} con cancelación programada
          </div>
          <div v-if="attentionTotal > 0" class="small text-muted mt-1">Llama al cliente.</div>
        </b-card>
      </b-col>

      <b-col cols="12" sm="6" lg="3" class="mb-3">
        <b-card class="h-100 border-0 shadow-sm">
          <div class="text-muted small">PatientFlow</div>
          <div class="h3 mb-1">{{ money(patientFlow.amount) }}</div>
          <div class="small text-muted">
            {{ patientFlow.generated || 0 }} de {{ patientFlow.attended || 0 }} citas asistidas generaron comisión
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12" lg="6" class="mb-3">
        <b-card class="h-100 border-0 shadow-sm">
          <div class="text-muted small mb-2">Progreso Élite</div>
          <div v-for="bar in eliteBars" :key="bar.key" class="mb-2">
            <div class="d-flex justify-content-between small">
              <span>{{ bar.label }}: {{ bar.progress.active }} de {{ bar.progress.threshold }}</span>
              <span v-if="bar.progress.reached" class="text-success font-weight-bold">Alcanzado · bono {{ money(bar.progress.bonus) }}</span>
              <span v-else class="text-muted">Faltan {{ bar.progress.remaining }} · bono {{ money(bar.progress.bonus) }}</span>
            </div>
            <b-progress :value="bar.percent" :variant="bar.progress.reached ? 'success' : 'primary'" height="0.6rem" />
          </div>
          <p v-if="!eliteBars.length" class="small text-muted mb-0">No hay una meta Élite vigente.</p>
        </b-card>
      </b-col>

      <b-col cols="12" lg="6" class="mb-3">
        <b-card class="h-100 border-0 shadow-sm">
          <div class="text-muted small mb-2">{{ commissionsTitle }}</div>
          <b-row>
            <b-col cols="4">
              <div class="small text-muted">Estimada del mes</div>
              <div class="h5">{{ money(commissions.estimated_current_month) }}</div>
            </b-col>
            <b-col cols="4">
              <div class="small text-muted">Por cobrar</div>
              <div class="h5">{{ money(pending.total) }}</div>
            </b-col>
            <b-col cols="4">
              <div class="small text-muted">Cobrado (histórico)</div>
              <div class="h5">{{ money(commissions.paid_total) }}</div>
            </b-col>
          </b-row>
          <div v-if="Number(pending.on_hold)" class="small text-warning">
            {{ money(pending.on_hold) }} retenido por una garantía abierta.
          </div>
          <ul v-if="payableDates.length" class="list-unstyled small mb-0 mt-1">
            <li v-for="row in payableDates" :key="row.payable_on">
              {{ money(row.amount) }} {{ row.payable ? 'disponible desde el' : 'se paga el' }} {{ row.payable_on }}
            </li>
          </ul>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BCard, BCol, BProgress, BRow } from 'bootstrap-vue'
import { elitePercent, money } from '@/core/utils/agentPlanDashboard'

/**
 * Tarjetas del resumen. La agencia arma el mismo objeto con sus totales y su comisión propia.
 * summary: { active, period, attention, elite, commissions, patient_flow }
 */
export default {
  name: 'AgentSummaryCards',
  components: { BCard, BCol, BProgress, BRow },
  props: {
    summary: { type: Object, required: true },
    commissionsTitle: { type: String, default: 'Comisiones' },
  },
  computed: {
    active() {
      return this.summary.active || {}
    },
    periodNew() {
      return (this.summary.period && this.summary.period.new) || {}
    },
    periodCanceled() {
      return (this.summary.period && this.summary.period.canceled) || {}
    },
    attention() {
      return this.summary.attention || {}
    },
    attentionTotal() {
      return (Number(this.attention.payment_failed) || 0) + (Number(this.attention.cancel_scheduled) || 0)
    },
    patientFlow() {
      return this.summary.patient_flow || {}
    },
    commissions() {
      return this.summary.commissions || {}
    },
    pending() {
      return this.commissions.pending || {}
    },
    payableDates() {
      return Array.isArray(this.pending.by_payable_on) ? this.pending.by_payable_on : []
    },
    eliteBars() {
      const elite = this.summary.elite || {}
      return [
        { key: 'members', label: 'Miembros', progress: elite.members },
        { key: 'families', label: 'Familias', progress: elite.families },
      ]
        .filter((bar) => bar.progress)
        .map((bar) => ({ ...bar, percent: elitePercent(bar.progress) }))
    },
  },
  methods: {
    money,
  },
}
</script>

<style lang="scss" scoped>
.attention-card {
  border-left: 4px solid #f64e60 !important;
}
</style>
